import { useState, useRef, useEffect } from 'react';
import { ArrowUpRight, MessageSquareWarning } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import LoginDialog from '@/components/Navbar/LoginDialog';
import { selectCurrentUser, setLogout } from '@/redux/auth/auth.slice';
import { useLogoutMutation } from '@/redux/auth/auth.api.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import fileFinityLogo from '@/assets/filefinity_navbar_logo.png';
import ProfilePicture from '../ui/profile-picture';
import VerifyAccountModal from '@/redux/auth/VerifyAccountModal';
import MobileNav from './MobileNav';
import ForgetPasswordDialog from '@/redux/auth/ForgetPassword/ForgetPasswordModal';
import GlobalSearch from '@/pages/Landing/GlobalSearch';

function Navbar() {
  const { isAuthenticated, user, isVerified } = useSelector(selectCurrentUser);
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(() => {
    return localStorage.getItem('hideWelcomeMessage') !== 'true';
  });
  const navRef = useRef<HTMLDivElement>(null);

  // Update height whenever banners change
  useEffect(() => {
    if (navRef.current) {
      const height = navRef.current.offsetHeight;
      document.documentElement.style.setProperty('--nav-height', `${height}px`);
    }
  }, [showWelcome, isAuthenticated, isVerified]);

  const handleDismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('hideWelcomeMessage', 'true');
  };

  async function handleLogout() {
    await logout();
    dispatch(setLogout());
    dispatch(setShowLoginDialog(false));
    navigate('/');
    toast.success('Logged out successfully');
  }

  const APPSMITH_URL = import.meta.env.VITE_APPSMITH_URL;
  return (
    <div ref={navRef} className="fixed top-0 z-50 w-full">
      <ForgetPasswordDialog variant="outline" />

      <div className="flex justify-between items-center w-full px-5 py-2 md:px-[5rem] bg-primary">
        <Link
          to="/"
          className="flex items-center justify-center gap-3 flex-shrink-0"
        >
          <img
            src={fileFinityLogo || '/placeholder.svg'}
            alt="filefinity logo"
            className="h-10 w-auto object-contain"
          />
        </Link>
        <div className="hidden md:flex gap-1 items-center w-full justify-end">
          <GlobalSearch className="mr-4 ml-6" />
          <div className="flex items-center">
            <a
              href="https://filefinity.featurebase.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-100 flex p-2"
            >
              News
            </a>
            <ArrowUpRight className="h-5 w-5 text-slate-100" />
          </div>
          {isAuthenticated ? (
            <>
              <Link to="/myprojects">
                <Button variant="default">My Projects</Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ProfilePicture
                    src={user?.profilePicture}
                    alt={user?.firstname}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 m-3">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to={`/${user?.username}`}>Profile</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/changePassword">Change Password</Link>
                    </DropdownMenuItem>
                    {user?.email.endsWith('@onefinitycnc.com') &&
                    user?.isVerified ? (
                      <DropdownMenuItem asChild>
                        <div className="flex items-center">
                          <a
                            href={APPSMITH_URL}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Admin Dashboard
                          </a>
                          <ArrowUpRight className="h-5 w-5" />
                        </div>
                      </DropdownMenuItem>
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <></>
                    )}
                    <DropdownMenuItem onClick={handleLogout}>
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <LoginDialog variant="ghost" />
              <Button variant="gradient" className="border" asChild>
                <Link to="/signup">Sign up</Link>
              </Button>
            </>
          )}
        </div>

        <MobileNav
          isAuthenticated={isAuthenticated}
          user={user}
          onLogout={handleLogout}
        />
      </div>

      {isAuthenticated && !isVerified && (
        <p className="w-full py-2 text-center text-base font-medium bg-[#FFEA99] flex items-center justify-center gap-2">
          <MessageSquareWarning className="w-5 h-5 animate-bounce text-red-500" />
          You have been added to the waitlist &nbsp;
          <VerifyAccountModal />
        </p>
      )}

      {showWelcome && (
        <div className="flex justify-between items-center text-sm text-muted-foreground font-secondary bg-[#FFEA99] py-2 px-5">
          <span className="flex-1 text-center">
            Welcome to Filefinity - Your One-Stop Destination for CNC router
            File Sharing!
          </span>
          <button
            type="button"
            className="p-0 h-auto text-inherit hover:bg-transparent"
            onClick={handleDismissWelcome}
            aria-label="Dismiss welcome message"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

export default Navbar;
